import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  List,
  ListItemText,
  Typography,
  Stack,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  CardMedia,
  Chip,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LoaderSpinner from "./LoaderSpinner";
import { useGlobalAuthContext } from "../context/AuthContext";
import { useProductsQuery } from "../Slices/roomNateApis";
import axios from "axios";
import { baseUrl } from "../utils/baseUrl";
import { ink } from "../theme/spatial";

const slideWidth = 200;
const marginRight = 10;

const PartnerShoppingList = ({ items, index, containerRef, furniture }) => {

  const [selectedPriceFilter] = useState("$");
  const [filterStateData, setFilterStateData] = useState({});

  const [selectedItem, setSelectedItem] = useState(null);
  const [lItemsArray, setLItemsArray] = useState([]);
  const [ids, setIds] = useState([]);

  const { currentUserSessionStorage, partner } = useGlobalAuthContext();
  const { data, refetch, isFetching } = useProductsQuery(
    { partner: partner, items: ids },
    {
      skip: !currentUserSessionStorage || ids.length === 0,
    }
  );

  function sortShoppingList(items) {
    const itemsList = items?.length >= 1 ? items : [];
    return [...itemsList]?.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      const isNumA = /^\d/.test(nameA);
      const isNumB = /^\d/.test(nameB);

      if (isNumA && !isNumB) return -1;
      if (!isNumA && isNumB) return 1;

      return nameA.localeCompare(nameB, undefined, { numeric: true });
    });
  }

  const accordionRefs = useRef([]);

  useEffect(() => {
    const cleanedFurnitureId = furniture?.substring(
      furniture?.indexOf("-") + 1
    );

    if (items?.[index]?.furniture) {
      const newLItemsArray = [];

      const specificFurniture = items[index].furniture.find(
        (item) => item._id === cleanedFurnitureId
      );

      if (specificFurniture) {
        const lItemsToProcess =
          specificFurniture?.lItems?.length > 0
            ? specificFurniture.lItems
            : [specificFurniture];

        lItemsToProcess?.forEach((lItem) => {
          const baseId = lItem?._id;
          const capacity =
            lItem.capacity && !isNaN(lItem.capacity) ? +lItem.capacity : 1;

          const existingItemIndex = newLItemsArray.findIndex(
            (existing) => existing._id === baseId
          );

          if (existingItemIndex !== -1) {
            newLItemsArray[existingItemIndex].capacity += capacity;
          } else {
            newLItemsArray.push({
              _id: baseId,
              name: lItem?.name || "No Name",
              frontViewImage: lItem?.frontViewImage || "no image",
              capacity,
              length: lItem?.height || 1,
              width: lItem?.width || 1,
            });
          }
        });
      } else {
        items[index].furniture.forEach((item) => {
          const lItemsToProcess =
            item?.lItems?.length > 0 ? item.lItems : [item];

          lItemsToProcess?.forEach((lItem) => {
            const baseId = lItem?._id;
            const capacity =
              lItem.capacity && !isNaN(lItem.capacity) ? +lItem.capacity : 1;

            const existingItemIndex = newLItemsArray.findIndex(
              (existing) => existing._id === baseId
            );

            if (existingItemIndex !== -1) {
              newLItemsArray[existingItemIndex].capacity += capacity;
            } else {
              newLItemsArray.push({
                _id: baseId,
                name: lItem?.name || "No Name",
                frontViewImage: lItem?.frontViewImage || "no image",
                capacity,
                length: lItem?.height || 1,
                width: lItem?.width || 1,
              });
            }
          });
        });
      }

      setLItemsArray(sortShoppingList(newLItemsArray));
    }
  }, [items, index, furniture]);

  useEffect(() => {
    const flattenedIds =
      (items?.[index] &&
        items[index].furniture?.flatMap((item) =>
          item?.lItems?.length > 0
            ? item.lItems.map((lItem) => lItem._id)
            : [item._id]
        )) ||
      [];

    const extractUniqueIds = (ids) => {
      const uniqueIds = new Set();
      ids.forEach((id) => {
        if (typeof id !== "string" || id.length === 0) return;
        const baseId = id.split("-")[0];
        if (!baseId) return;
        uniqueIds.add(baseId);
      });
      return Array.from(uniqueIds);
    };

    const uniqueIds = extractUniqueIds(flattenedIds);

    setIds(uniqueIds);
  }, [items, index]);

  useEffect(() => {
    if (!currentUserSessionStorage || ids.length === 0) return;
    refetch();
  }, [ids, refetch, currentUserSessionStorage]);

  const filteredIframes = React.useCallback((dynamicItemId) =>
    data?.filter((product) => {
      return product?.items?.includes(dynamicItemId);
    }), [data]);

  const calculatePercentiles = React.useCallback((data) => {
    if (data?.length < 1) {
      return;
    }
    const prices = data?.map((item) => item.price ?? 0)?.sort((a, b) => a - b);

    const percentile33 = prices?.[Math.floor((prices.length * 33) / 100)];
    const percentile66 = prices?.[Math.floor((prices.length * 66) / 100)];
    const percentile100 = prices?.[prices.length - 1];

    return { percentile33, percentile66, percentile100 };
  }, []);

  const filterDataByPrice = React.useCallback((data, filter) => {
    if (data?.length < 1) {
      return;
    }
    const { percentile33, percentile66 } =
      calculatePercentiles(data);

    let filteredData = [];

    if (filter === "$") {
      filteredData = data.filter((item) => (item.price ?? 0) <= percentile33);
    } else if (filter === "$$") {
      filteredData = data.filter(
        (item) =>
          (item.price ?? 0) > percentile33 && (item.price ?? 0) <= percentile66
      );
    } else if (filter === "$$$") {
      filteredData = data.filter((item) => (item.price ?? 0) > percentile66);
    }

    return filteredData;
  }, [calculatePercentiles]);

  useEffect(() => {
    const updatedState = lItemsArray?.reduce((acc, item, index) => {
      const dynamicItemId = item._id;
      const filteredData = filteredIframes(dynamicItemId);
      const filteredDataPrice = filterDataByPrice(
        filteredData || [],
        selectedPriceFilter
      );

      acc[index] = filteredDataPrice;
      return acc;
    }, {});

    setFilterStateData(updatedState);
  }, [lItemsArray, data, selectedPriceFilter, filteredIframes, filterDataByPrice]);

  const handleAccordionChange = (index) => {
    setSelectedItem((prev) => (prev === index ? null : index));

    setTimeout(() => {
      const element = accordionRefs.current[index];
      const container = containerRef.current;

      if (element && container) {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const scrollOffset =
          rect.top - containerRect.top + container.scrollTop - 50;

        container.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    }, 500);
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
        }}
      >
        <List
          sx={{
            py: 0,
            position: "relative",
          }}
        >
          {lItemsArray?.map((item, index) => {
            const filteredDataPrice = filterStateData[index] || [];
            const currentUrl = window.location.href;

            const parts = currentUrl?.split("//")[1]?.split("/");

            const domain = parts?.[0];

            const domains = [];

            return (
              <div
                key={index}
                style={{
                  width: "100%",
                  borderBottom:
                    selectedItem === index ? "1px solid #e0e0e0" : "none",
                }}
                ref={(el) => (accordionRefs.current[index] = el)}
              >
                <Accordion
                  expanded={selectedItem === index}
                  disableGutters
                  sx={{
                    boxShadow: "none",
                    backgroundColor: "transparent",
                  }}
                >
                  <AccordionSummary
                    aria-controls={`panel${selectedItem}-content`}
                    id={`panel${selectedItem}-content`}
                    expandIcon={
                      selectedItem ===
                      index ? null : 
                      domains.includes(domain) ? null : isFetching ? (
                        <LoaderSpinner text={false} />
                      ) : filteredDataPrice?.length === 0 ? null : (
                        <Button
                          variant="text"
                          disableRipple
                          sx={{
                            textTransform: "none",
                            color: ink.primary,
                            p: 0,
                            "& .MuiButton-endIcon": {
                              marginLeft: "4px",
                            },
                          }}
                          endIcon={<ExpandMore />}
                          onClick={() => handleAccordionChange(index)}
                        >
                          Shop
                        </Button>
                      )
                    }
                    sx={{
                      borderBottom:
                        selectedItem !== index ? "1px solid #e0e0e0" : "none",
                      padding: "8px 0",
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        transform: "rotate(0deg) !important",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          flexDirection="row"
                          alignItems="center"
                          gap={2}
                          justifyContent="space-between"
                        >
                          <>
                            <Typography
                              variant="body1"
                              sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "19.6px",
                                textAlign: "left",
                              }}
                            >
                              {item.name}
                            </Typography>
                          </>
                        </Stack>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#20212480",
                            fontSize: "12px",
                            textAlign: "left",
                          }}
                        >
                          Qty: {item.capacity || 1}
                        </Typography>
                      }
                    />
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "12px",
                        overflowX: "auto",
                        padding: "0px 0px 16px 0px",
                        whiteSpace: "nowrap",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        msOverflowStyle: "none",
                      }}
                    >
                      {filteredDataPrice.length === 0 ? (
                        <Typography variant="body2">
                          No product exists with this filter.
                        </Typography>
                      ) : (
                        filteredDataPrice.map((product, idx) => (
                          <div
                            key={product}
                            style={{
                              width: `${slideWidth}px`,
                              marginRight:
                                idx !== data?.length - 1
                                  ? `${marginRight}px`
                                  : 0,
                              pointerEvents: false ? "none" : "auto",
                            }}
                          >
                              <Box sx={{bgcolor:"white" ,cursor:"pointer", border:"0.5px solid #dadada"}}
                               onClick={() => {
                                  let url = product?.url;
                                  if (
                                    product?.partnerDetails?.referralCode &&
                                    product?.partnerDetails?.referralLabel
                                  )
                                    url += `?${product?.partnerDetails?.referralLabel}=${product?.partnerDetails?.referralCode}`;
                                  window.open(url, "_blank");
                                  try {
                                    if (process.env.NODE_ENV !== 'development') {
                                      axios.post(
                                        `${baseUrl}/products/trackEngagement`,
                                        {
                                          product: product?._id,
                                        },
                                        {
                                          headers: {
                                            Authorization: `Bearer ${currentUserSessionStorage}`,
                                          },
                                        }
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error tracking engagement:",
                                      error.response
                                        ? error.response.data
                                        : error.message
                                    );
                                  }
                                }}>
                                  
                                <CardMedia
                                  component="img"
                                  sx={{
                                    height: 200,
                                    objectFit: "contain",
                                  }}
                                  image={product?.image}
                                  alt="green iguana"
                                />
                                  
                                  <Stack  py={2} px={1}>
                                  <Stack flexDirection={"row"} sx={{ mb: 1 }} alignItems={"center"} gap={1}>
                                    {product?.colors.slice(0, 3)?.map((color) => (
                                      <Box key={color}>
                                        <Chip
                                          style={{
                                            boxShadow: "0 0 0 2px #fff, 0 0 0 3px #bdbdbd",
                                            backgroundColor: color,
                                            borderRadius: "50%",
                                            width: 14,
                                            height: 14,
                                            cursor: "pointer",
                                          }}
                                        />
                                      </Box>
                                    ))}
                                    {product?.colors?.length > 3 && (
                                      <Typography sx={{fontSize:"12px"}}>
                                         +{product.colors.length - 3}
                                      </Typography>
                                    )}
                                  </Stack>

                                  <Typography
                                  sx={{fontWeight:"500" , fontSize:"16px" , mb:0.5 ,textWrap:"auto" , wordBreak:"break-word"}}
                                  
                                  >
                                    {product?.name}
                                  </Typography>

                                  {product?.price &&
                                  <Typography
                                    sx={{ fontWeight:"500" , fontSize:"15px" }}
                                  >
                                    {"$" + product?.price}
                                  </Typography>
                                  }

                                  </Stack>
                                </Box>
                          </div>
                        ))
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default PartnerShoppingList;
